import { useSearchParams } from '@remix-run/react';
import { useCallback, useMemo } from 'react';

import { FiltersFormValues } from '~/schemas/filter.schema';
import { FilterDefinition } from '~/types/Filter';

const facetKey = (key: string) => `facets[${key}][]`;
const facetMinKey = (key: string) => `facets[${key}][min]`;
const facetMaxKey = (key: string) => `facets[${key}][max]`;

export const useProductFilters = (filterDefinitions: FilterDefinition[] = []) => {
  const safeDefinitions = useMemo(
    () => (Array.isArray(filterDefinitions) ? filterDefinitions : []),
    [filterDefinitions]
  );
  const [searchParams, setSearchParams] = useSearchParams();

  const readFiltersFromParams = useCallback(
    (params: URLSearchParams) => {
      const values: FiltersFormValues = {};

      safeDefinitions.forEach((filter) => {
        if ('buckets' in filter) {
          const selected = params.getAll(facetKey(filter.key));
          if (selected.length > 0) {
            values[filter.key] = selected;
          }
        }

        if ('min' in filter) {
          const min = params.get(facetMinKey(filter.key));
          const max = params.get(facetMaxKey(filter.key));
          if (min !== null && max !== null) {
            const minNum = Number(min);
            const maxNum = Number(max);
            if (!Number.isNaN(minNum) && !Number.isNaN(maxNum)) {
              values[filter.key] = [minNum, maxNum];
            }
          }
        }
      });

      return values;
    },
    [safeDefinitions]
  );

  const activeFilters = useMemo(() => readFiltersFromParams(searchParams), [searchParams, readFiltersFromParams]);

  const filterKeys = useMemo(() => new Set(safeDefinitions.map((f) => f.key)), [safeDefinitions]);

  const deleteFacetParams = (params: URLSearchParams, key: string) => {
    params.delete(facetKey(key));
    params.delete(facetMinKey(key));
    params.delete(facetMaxKey(key));
  };

  const applyFilters = useCallback(
    (filters: FiltersFormValues) => {
      const prevParams = new URLSearchParams(searchParams);
      const nextParams = new URLSearchParams(prevParams);

      safeDefinitions.forEach((filter) => {
        deleteFacetParams(nextParams, filter.key);
      });

      Object.entries(filters).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          if (value.length === 0) return;

          if (value.length === 2 && typeof value[0] === 'number') {
            const def = safeDefinitions.find((f) => f.key === key);

            if (def && 'min' in def && value[0] === def.min && value[1] === def.max) {
              return;
            }

            nextParams.set(facetMinKey(key), String(value[0]));
            nextParams.set(facetMaxKey(key), String(value[1]));
            return;
          }

          (value as string[]).forEach((v) => {
            nextParams.append(facetKey(key), v);
          });
        }
      });

      const stripPage = (params: URLSearchParams) => {
        const copy = new URLSearchParams(params);
        copy.delete('page');
        return copy.toString();
      };

      const filtersChanged = stripPage(prevParams) !== stripPage(nextParams);

      if (filtersChanged) {
        nextParams.delete('page');
      }

      if (nextParams.toString() !== prevParams.toString()) {
        setSearchParams(nextParams, { replace: true, preventScrollReset: true });
      }
    },
    [searchParams, setSearchParams, safeDefinitions]
  );

  const removeFilter = useCallback(
    (key: string, valueToRemove?: string) => {
      const next = new URLSearchParams(searchParams);

      if (!valueToRemove) {
        deleteFacetParams(next, key);
      } else {
        const current = next.getAll(facetKey(key));
        next.delete(facetKey(key));
        current.filter((v) => v !== valueToRemove).forEach((v) => next.append(facetKey(key), v));
      }

      next.delete('page');
      setSearchParams(next, { preventScrollReset: true });
    },
    [searchParams, setSearchParams]
  );

  const filterLabelMap = useMemo(() => {
    const map: Record<string, string> = {};
    safeDefinitions.forEach((f) => {
      map[f.key] = f.label;
    });
    return map;
  }, [safeDefinitions]);

  const optionLabelMap = useMemo(() => {
    const map: Record<string, Record<string, string>> = {};

    safeDefinitions.forEach((filter) => {
      if ('buckets' in filter) {
        map[filter.key] = {};
        filter.buckets.forEach((opt) => {
          map[filter.key][opt.key] = opt.label;
        });
      }
    });

    return map;
  }, [safeDefinitions]);

  const currencyMap = useMemo(() => {
    const map: Record<string, string> = {};
    safeDefinitions.forEach((f) => {
      if ('currency' in f) {
        map[f.key] = f.currency;
      }
    });
    return map;
  }, [safeDefinitions]);

  const clearFilters = useCallback(() => {
    const params = new URLSearchParams();
    params.delete('page');
    setSearchParams(params, { preventScrollReset: true });
  }, [setSearchParams]);

  return {
    activeFilters,
    applyFilters,
    removeFilter,
    filterLabelMap,
    optionLabelMap,
    currencyMap,
    clearFilters,
    filterKeys,
  };
};
